import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import useWindowFocus from 'use-window-focus';

import { admin, superuser } from '../lib/config/roles-and-permissions';
import { useUnreadReportsCountQuery } from '../lib/graphql';
import Guard from './guard';
import { Spacer } from './spacer';
import { IconButton, IconButtonSecondary } from './styled/button';
import Logo from './styled/logo';

const animateSidenav = keyframes`
  0% {
    transform: translateX(-16rem);
    box-shadow: 0px 0px 0px rgba(0, 0, 0, 0);
  }
  100% {
    transform: translateX(0rem);
    box-shadow: 0px 16px 32px rgba(0, 0, 0, 0.08), 0px 8px 16px rgba(0, 0, 0, 0.08), 0px 4px 8px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.08);
  }
`;

const SidenavWrapper = styled.section`
  position: fixed;
  top: 0;
  width: 16rem;
  transform: translateX(-16rem);
  /* min-height: 100%; */
  min-height: -webkit-fill-available;
  /* height: 100%; */
  max-height: 100vh;
  border-right: 1px solid ${(props) => props.theme.stroke};
  background-color: ${(props) => props.theme.noContrast};
  animation: ${(props) => (props.show ? animateSidenav : 'none')} 0.25s ease-out
    forwards;
  z-index: 2;
  @media (min-width: ${(props) => props.theme.medium}) {
    position: sticky;
    width: 100%;
    min-height: 100vh;
    height: 100vh;
    max-height: 100vh;
    box-shadow: none;
    animation: none;
    transform: none;
  }
`;

const SidenavLogo = styled.div`
  height: 6rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid ${(props) => props.theme.stroke};
`;

const SidenavMenu = styled.nav`
  padding: 1rem;
`;

const Menu = styled.ul`
  margin: 0;
  padding: 0;
  list-style-type: none;
`;

const MenuItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  list-style: none;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 0.75rem;
  line-height: 1.5rem;
  letter-spacing: 0.03em;
  border-radius: 0.25rem;
  color: ${(props) =>
    props.active ? props.theme.noContrast : props.theme.contrast};
  background-color: ${(props) =>
    props.active ? props.theme.primary : props.theme.noContrast};
  cursor: pointer;
  transition: 0.225s all;
  :hover {
    background-color: ${(props) =>
      props.active ? props.theme.primaryHover : props.theme.noContrastHover};
  }
`;

const SidenavMobile = styled(IconButton)`
  position: fixed;
  right: -3.5rem;
  bottom: 1rem;
  z-index: 1;
  @media (min-width: ${(props) => props.theme.medium}) {
    display: none;
  }
`;

const SidenavBottomActions = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 1rem;
`;

function SidenavMenuItem(props) {
  return (
    <>
      <Link href={props.href}>
        <a>
          <MenuItem active={props.active}>
            <span className="material-icons">{props.icon}</span>
            <Spacer size={1} />
            {props.children}
          </MenuItem>
        </a>
      </Link>
      <Spacer size={0.5} />
    </>
  );
}

const notifications = 100;

export default function Sidenav() {
  const router = useRouter();
  const [location, setLocation] = useState('');
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    setLocation(router.pathname);
  }, [router]);

  return (
    <SidenavWrapper show={showMenu}>
      <SidenavMobile onClick={() => setShowMenu(!showMenu)}>
        {showMenu ? (
          <span className="material-icons">close</span>
        ) : (
          <span className="material-icons">menu</span>
        )}
      </SidenavMobile>
      <SidenavLogo>
        <Logo width={160} height={23.92} />
      </SidenavLogo>
      <SidenavMenu>
        <Menu>
          <SidenavMenuItem
            icon="dashboard"
            href="/dashboard"
            active={location.startsWith('/dashboard')}
          >
            Dashboard
          </SidenavMenuItem>
          <Guard
            permissions={superuser}
            render={() => (
              <SidenavMenuItem
                icon="maps_home_work"
                href="/organisations"
                active={location.startsWith('/organisations')}
              >
                Organisaties
              </SidenavMenuItem>
            )}
          />
          <SidenavMenuItem
            icon="storage"
            href="/knowledge-base"
            active={location.startsWith('/knowledge-base')}
          >
            Kennisbank
          </SidenavMenuItem>
          <SidenavMenuItem
            icon="local_activity"
            href="/activities"
            active={location.startsWith('/activities')}
          >
            Activiteiten
          </SidenavMenuItem>
          <Guard
            permissions={admin}
            render={() => (
              <SidenavMenuItem
                icon="maps_home_work"
                href={`/my-organisation`}
                active={location.startsWith('/my-organisation')}
              >
                Mijn organisatie
              </SidenavMenuItem>
            )}
          />

          <SidenavMenuItem
            icon="face"
            href="/profile"
            active={location.startsWith('/profile')}
          >
            Mijn profiel
          </SidenavMenuItem>
          <Guard
            permissions={superuser}
            render={() => (
              <SidenavMenuItem
                icon="settings"
                href="/admin"
                active={location.startsWith('/admin')}
              >
                Beheer
              </SidenavMenuItem>
            )}
          />
          <Spacer size={1} />
          <SidenavMenuItem icon="logout" href="/api/auth/logout">
            Uitloggen
          </SidenavMenuItem>
        </Menu>
      </SidenavMenu>
      <SidenavBottomActions>
        <Guard permissions={superuser} render={() => <NotificationCount />} />
      </SidenavBottomActions>
    </SidenavWrapper>
  );
}

function NotificationCount() {
  const [{ data }, refresh] = useUnreadReportsCountQuery();
  const router = useRouter();

  const isWindowFocused = useWindowFocus();

  // TODO: This should have a smarter update
  useEffect(() => {
    if (isWindowFocused) {
      refresh({ requestPolicy: 'cache-and-network' });
    }
  }, [isWindowFocused, refresh]);

  return (
    <IconButtonSecondary
      onClick={() => router.push('/notifications')}
      notifications={
        data?.unreadReportsCount > 0 ? data?.unreadReportsCount : ''
      }
    >
      <span className="material-icons">
        {data?.unreadReportsCount ? 'notifications_active' : 'notifications'}
      </span>
    </IconButtonSecondary>
  );
}
